import supabaseClient from '@/api/supabaseClient.ts';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import type { AuditExportResponse } from '$/requests/AuditExport.ts';
import type { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

async function fetchAuditExport(decisionId: string): Promise<AuditExportResponse> {
  const { data, error } = await supabaseClient.functions.invoke('audit-export', {
    body: { decision_id: decisionId, format: 'json' },
  });

  if (error) {
    if (error instanceof FunctionsHttpError) {
      const errorResponse: BackendErrorResponse = await error.context.json();
      throw new Error(errorResponse.errors?.join(', ') ?? 'Export failed');
    }
    throw new Error(error.message || 'Export failed');
  }

  const payload = data as { data?: AuditExportResponse; errors?: string[] };
  if (payload?.errors?.length) {
    throw new Error(payload.errors.join(', '));
  }
  if (!payload?.data) {
    throw new Error('Export failed');
  }

  return payload.data;
}

function downloadJsonExport(decisionId: string, payload: AuditExportResponse): void {
  const date = new Date().toISOString().slice(0, 10);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `COMMAND_ExecutionRecord_${decisionId}_${date}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

const useAuditExport = () =>
  useMutation({
    mutationFn: async (decisionId: string) => {
      const payload = await fetchAuditExport(decisionId);
      downloadJsonExport(decisionId, payload);
      return payload;
    },
  });

export default useAuditExport;
